import axios, { AxiosResponse } from 'axios';
import env from '../config/env';

// Extend Window interface for Stripe
declare global {
  interface Window {
    Stripe: any;
  }
}

// Types
export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
  fingerprint?: string;
  funding?: string;
  country?: string;
  createdAt?: Date;
}

export interface PaymentIntent {
  clientSecret: string;
  paymentIntentId: string;
  status: string;
  requiresAction?: boolean;
}

export interface SetupIntent {
  clientSecret: string;
  setupIntentId: string;
  status: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code?: string;
  };
}

export interface CreatePaymentIntentRequest {
  orderId: string;
  paymentMethodId?: string;
  saveCard?: boolean;
}

export interface BillingDetails {
  name: string;
  email?: string;
  phone?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state?: string;
    postal_code: string;
    country: string;
  };
}

class PaymentService {
  private baseURL: string;
  private stripe: any = null;
  private elements: any = null;

  constructor() {
    this.baseURL = env.API_URL;
  }

  // Get authorization headers
  private getAuthHeaders(): { Authorization: string } | {} {
    const token = localStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Initialize Stripe
  async initializeStripe(): Promise<any> {
    if (this.stripe) return this.stripe;

    try {
      // Load Stripe.js if not already loaded
      if (!window.Stripe) {
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        document.head.appendChild(script);
        
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
        });
      }

      // Get publishable key from environment or backend
      const publishableKey = env.STRIPE_PUBLISHABLE_KEY || 
                           await this.getStripePublishableKey();
      
      this.stripe = (window as any).Stripe(publishableKey);
      return this.stripe;
    } catch (error: any) {
      console.error('Failed to initialize Stripe:', error);
      throw error;
    }
  }

  // Get Stripe publishable key from backend
  async getStripePublishableKey(): Promise<string> {
    try {
      const response: AxiosResponse<{ publishableKey: string }> = await axios.get(
        `${this.baseURL}/payments/config`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.publishableKey;
    } catch (error: any) {
      console.error('Failed to get Stripe config:', error);
      throw error;
    }
  }

  // Create Stripe Elements
  async createElements(options: any = {}): Promise<any> {
    const stripe = await this.initializeStripe();
    
    const defaultOptions = {
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#f6cf02',
          colorBackground: '#ffffff',
          colorText: '#0d0d0d',
          colorDanger: '#df1b41',
          fontFamily: 'Inter, system-ui, sans-serif',
          spacingUnit: '4px',
          borderRadius: '8px'
        }
      },
      ...options
    };

    this.elements = stripe.elements(defaultOptions);
    return this.elements;
  }

  // Create card element
  async createCardElement(options: any = {}): Promise<any> {
    const elements = await this.createElements();
    
    const defaultOptions = {
      style: {
        base: {
          fontSize: '16px',
          color: '#0d0d0d',
          fontFamily: 'Inter, system-ui, sans-serif',
          '::placeholder': {
            color: '#6b7280'
          }
        },
        invalid: {
          color: '#df1b41',
          iconColor: '#df1b41'
        }
      },
      hidePostalCode: false,
      ...options
    };

    return elements.create('card', defaultOptions);
  }

  // Create payment intent
  async createPaymentIntent(request: CreatePaymentIntentRequest): Promise<ApiResponse<PaymentIntent>> {
    try {
      const response: AxiosResponse<ApiResponse<PaymentIntent>> = await axios.post(
        `${this.baseURL}/payments/create-intent`,
        request,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error.response?.data?.error || 'Failed to create payment intent'
        }
      };
    }
  }

  // Pay with saved card
  async payWithSavedCard(orderId: string, paymentMethodId: string): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await axios.post(
        `${this.baseURL}/payments/pay-with-saved-card`,
        {
          orderId,
          paymentMethodId
        },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error.response?.data?.error || 'Payment failed'
        }
      };
    }
  }

  // Confirm payment intent
  async confirmPaymentIntent(paymentIntentId: string, paymentMethodId?: string): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await axios.post(
        `${this.baseURL}/payments/confirm-intent`,
        {
          paymentIntentId,
          paymentMethodId
        },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error.response?.data?.error || 'Failed to confirm payment'
        }
      };
    }
  }

  // Get saved payment methods
  async getSavedPaymentMethods(): Promise<ApiResponse<PaymentMethod[]>> {
    try {
      const response: AxiosResponse<ApiResponse<PaymentMethod[]>> = await axios.get(
        `${this.baseURL}/payments/payment-methods`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error.response?.data?.error || 'Failed to get payment methods'
        }
      };
    }
  }

  // Save payment method
  async savePaymentMethod(paymentMethod: any, setAsDefault: boolean = false): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await axios.post(
        `${this.baseURL}/payments/save-payment-method`,
        {
          paymentMethodId: paymentMethod.id,
          setAsDefault
        },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error.response?.data?.error || 'Failed to save payment method'
        }
      };
    }
  }

  // Delete payment method
  async deletePaymentMethod(paymentMethodId: string): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await axios.delete(
        `${this.baseURL}/payments/payment-method/${paymentMethodId}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error.response?.data?.error || 'Failed to delete payment method'
        }
      };
    }
  }

  // Set default payment method
  async setDefaultPaymentMethod(paymentMethodId: string): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await axios.put(
        `${this.baseURL}/payments/default-method`,
        {
          paymentMethodId
        },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error.response?.data?.error || 'Failed to set default payment method'
        }
      };
    }
  }

  // Create setup intent
  async createSetupIntent(): Promise<ApiResponse<SetupIntent>> {
    try {
      const response: AxiosResponse<ApiResponse<SetupIntent>> = await axios.post(
        `${this.baseURL}/payments/setup-intent`,
        {},
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error.response?.data?.error || 'Failed to create setup intent'
        }
      };
    }
  }

  // Create payment method for saving
  async createPaymentMethod(cardElement: any, billingDetails: BillingDetails): Promise<any> {
    const stripe = await this.initializeStripe();
    
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: billingDetails
    });

    if (error) {
      throw error;
    }

    return paymentMethod;
  }

  // Confirm setup intent
  async confirmSetupIntent(clientSecret: string, paymentMethodId: string): Promise<any> {
    const stripe = await this.initializeStripe();
    
    const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: paymentMethodId
    });

    if (error) {
      throw error;
    }

    return setupIntent;
  }

  // Confirm payment with saved payment method
  async confirmPaymentWithSavedMethod(clientSecret: string, paymentMethodId: string): Promise<any> {
    const stripe = await this.initializeStripe();
    
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethodId
    });

    if (error) {
      throw error;
    }

    return paymentIntent;
  }

  // Confirm payment with new card
  async confirmPaymentWithNewCard(clientSecret: string, cardElement: any, billingDetails: Partial<BillingDetails> = {}): Promise<any> {
    const stripe = await this.initializeStripe();
    
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: billingDetails
      }
    });

    if (error) {
      throw error;
    }

    return paymentIntent;
  }

  // Format card brand for display
  formatCardBrand(brand: string): string {
    const brands: { [key: string]: string } = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'American Express',
      discover: 'Discover',
      diners: 'Diners Club',
      jcb: 'JCB',
      unionpay: 'UnionPay',
      unknown: 'Unknown'
    };
    
    return brands[brand] || brands.unknown;
  }

  // Get card icon
  getCardIcon(brand: string): string {
    const icons: { [key: string]: string } = {
      visa: '💳',
      mastercard: '💳',
      amex: '💳',
      discover: '💳',
      diners: '💳',
      jcb: '💳',
      unionpay: '💳',
      unknown: '💳'
    };
    
    return icons[brand] || icons.unknown;
  }

  // Cleanup
  destroy(): void {
    this.elements = null;
    this.stripe = null;
  }
}

export default new PaymentService();